import { useRef } from 'react'

import { Code, ConnectError } from '@connectrpc/connect'
import { BiddingApprovalMode } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'

import { Alert, Text, VStack } from '@enechain/ecloud-designsystem'

import { BidCsvDateCounts } from '~/components/bidding/common/BidCsvDateCounts'
import { PageBody } from '~/components/common/PageBody'
import { PageLayout } from '~/components/common/PageLayout'
import { UploadWizard } from '~/components/upload/UploadWizard'
import { MAX_CSV_FILE_SIZE_BYTES } from '~/constants/csvUpload'
import { useBidCsvImportReturn } from '~/hooks/useBidCsvImportReturn'
import { useWorkflowCaseIdParam } from '~/hooks/workflowCase/useWorkflowCaseIdParam'
import { downloadCsv } from '~/libs/downloadCsv'
import {
  type BidCsvDateCount,
  toBidCsvDateCounts,
} from '~/models/bidCsvDateCount'
import {
  BLOCK_BID_CSV_HEADER,
  BLOCK_BID_CSV_LAYOUTS,
  BLOCK_CSV_TYPES,
  SPOT_BID_NORMAL_CSV_HEADER,
  SPOT_BID_NORMAL_CSV_HEADER_EN,
  blockCsvLayoutOf,
} from '~/models/bidCsvHeader'
import {
  type BidCsvImportNotice,
  buildSpotBlockNotice,
  buildSpotNormalNotice,
} from '~/models/bidCsvImportReturn'
import { civilDateToInputValue } from '~/models/civilDate'
import { WORKFLOW_ID_PARAM } from '~/models/workflowIdParam'
import { blockWriteBlockedReasonMessage } from '~/pages/SpotBidding/models/blockBidWrite'
import { spotWriteBlockedReasonMessage } from '~/pages/SpotBidding/models/spotBidWrite'
import { ConfirmRejection } from '~/pages/SpotCsvImport/components/ConfirmRejection'
import {
  type PreviewDataColumn,
  PreviewTable,
} from '~/pages/SpotCsvImport/components/PreviewTable'
import {
  useApplyBlockCsv,
  useApplySpotNormalCsv,
  useCheckBlockCsv,
  useCheckSpotNormalCsv,
} from '~/pages/SpotCsvImport/hooks/useBidCsvMutations'
import {
  type BidCsvKind,
  bidCsvKindFromSearchParam,
  blockCsvTypeFromSearchParam,
  toBlockPreviewRows,
  toSpotPreviewRows,
} from '~/pages/SpotCsvImport/models/bidCsvImport'
import {
  blockBidCsvTemplateFileName,
  buildBlockCsvTemplate,
  buildSpotNormalCsvTemplate,
  spotNormalCsvTemplateFileName,
} from '~/pages/SpotCsvImport/models/bidCsvTemplate'
import { blockBidCsvColumnGuide } from '~/pages/SpotCsvImport/models/blockBidCsvColumnGuide'
import { routePaths } from '~/routes/routePaths'

type CheckResult = {
  checkToken: string
  rows: ReturnType<typeof toSpotPreviewRows>
  /** 取込対象の件数 (通常入札 = 札数、 ブロック入札 = グループ数)。 */
  importableCount: number
  errorCount: number
  /** 通常入札の受渡日ごとの取込対象件数 (AC-SPOT-014)。 ブロック入札は空。 */
  dateCounts: BidCsvDateCount[]
  /**
   * 手数料自動考慮（スポット）が ON で手数料単価を取得できていない
   * (`bidding/spot-bidding.md` AC-BSP-004b)。 true の間は確定操作を無効化する
   * (行のエラーではなく取込全体の可否を示すフラグ)。
   */
  feeAutoConsiderUnavailable: boolean
  /**
   * 既存の札と重複する行の件数 (通常入札 = 行数、 ブロック入札 = グループ数。 リンクブロック・ループはペアで 1)。
   * 警告だけで確定は止めない (AC-SPOT-022 / AC-DSB-034)。
   */
  duplicateCount: number
}

type ApplyResult = {
  /** 入札画面へ移った先の着地 (タブ・受渡日・エリア) と成功のトーストの件数 (AC-SPOT-024) */
  notice: BidCsvImportNotice
}

/**
 * スポット入札 CSV インポート画面
 * (`/bidding/spot/csv-import?kind=normal|block`、 design `bidding/spot-csv-import.html`)。
 *
 * 配置はデータ取込ハブ (`admin/data/uploads/index.md`) ではなく **スポット入札画面の
 * 子ページ** (spec spot-bid-normal-csv-import.md §5、 2026-07-23 入札相談会確定。 入札 CSV は
 * データ更新目的ではなく入札作成操作のため)。 通常入札とブロック入札は列構成が
 * 全く異なるが、 ウィザードの骨格・確定後の挙動が同一なので `?kind` で 1 画面を
 * 共有する (spec spot-bid-block-csv-import.md §6)。
 *
 * Step 構成・エラーハンドリング・check_token の扱いは共通の `UploadWizard`
 * (= `admin/data/uploads/common.md` の実装) に委譲し、 本画面は種別ごとの
 * Check/Apply RPC・列ガイド・プレビュー整形だけを持つ。
 */
export const SpotCsvImportPage = (): React.ReactElement => {
  const { t, i18n } = useTranslation()
  const [searchParams] = useSearchParams()
  // 戻り先の入札画面でも読み取り専用表示を保つため、 `?workflowId=` を付ける (AC-WFW-014)。
  const caseId = useWorkflowCaseIdParam()
  const spotPagePath =
    caseId !== undefined
      ? `${routePaths.biddingSpot.path}?${new URLSearchParams({ [WORKFLOW_ID_PARAM]: caseId }).toString()}`
      : routePaths.biddingSpot.path
  const returnToBidPage = useBidCsvImportReturn(spotPagePath)
  // check の引換券ごとに、 確定時に apply 応答の件数から取込結果を作る関数。
  // UploadWizard の `onComplete` は check 結果を受け取らないので、 ここへ持つ。
  // 最新の 1 件だけにしない: 確認中に Step1 へ戻って別のファイルを選べる (AC-DUC-034) ので、
  // 前のファイルの check が後から返ると、 画面が確定する引換券より新しい値で上書きされる。
  const noticeBuildersRef = useRef(
    new Map<string, (appliedCount: number) => BidCsvImportNotice>(),
  )

  const kind: BidCsvKind = bidCsvKindFromSearchParam(searchParams.get('kind'))
  const blockCsvType = blockCsvTypeFromSearchParam(
    searchParams.get('blockType'),
  )

  const checkSpot = useCheckSpotNormalCsv()
  const applySpot = useApplySpotNormalCsv()
  const checkBlock = useCheckBlockCsv()
  const applyBlock = useApplyBlockCsv()

  const isBlock = kind === 'block'
  const blockLayout = blockCsvLayoutOf(blockCsvType)
  const expectedHeader = isBlock
    ? BLOCK_BID_CSV_HEADER.ja[blockLayout]
    : SPOT_BID_NORMAL_CSV_HEADER
  // 通常入札・ブロック入札とも日本語・英語どちらのヘッダーも受理する
  // (AC-SPOT-001 / AC-DSB-013、 common.md §1)。 ガイダンスとヘッダー不一致の
  // エラーは表示言語の列名で示す。 雛形は通常入札・ブロック入札とも表示言語のヘッダーで出す
  // (下の `handleTemplateDownload` / `blockTemplateDownloads`)。
  const englishHeader = isBlock
    ? BLOCK_BID_CSV_HEADER.en[blockLayout]
    : SPOT_BID_NORMAL_CSV_HEADER_EN
  // ブロック入札は、 どの導線から入っても通常ブロック (56 列)・リンクブロック・ループ
  // (58 列) の日本語・英語 4 種類を受理する (AC-DSB-013。 列構成は backend がヘッダーで
  // 決める)。 `expectedHeader` / `englishHeader` は導線の列構成で、 Step1 の案内と
  // ヘッダー不一致のエラーに示す列名にだけ使う。
  const acceptedHeaders = isBlock
    ? {
        ja: BLOCK_BID_CSV_LAYOUTS.map(
          (layout) => BLOCK_BID_CSV_HEADER.ja[layout],
        ),
        en: BLOCK_BID_CSV_LAYOUTS.map(
          (layout) => BLOCK_BID_CSV_HEADER.en[layout],
        ),
      }
    : { ja: [SPOT_BID_NORMAL_CSV_HEADER], en: [SPOT_BID_NORMAL_CSV_HEADER_EN] }
  const isEnglish = i18n.language.startsWith('en')
  const displayHeader = isEnglish ? englishHeader : undefined
  // 通常入札・ブロック入札とも 1 行ヘッダー (spot-bid-normal-csv-import.md §1 /
  // spot-bid-block-csv-import.md §1)。 発注主体区分は両方とも CSV の行ごとの列で持つので、
  // 画面で発注主体を選ばせない。

  const handleCheck = async (file: File): Promise<CheckResult> => {
    const csvContent = new Uint8Array(await file.arrayBuffer())
    if (isBlock) {
      // 発注主体区分は CSV の行ごとの列 (spec spot-bid-block-csv-import.md AC-DSB-016) なので
      // 画面の発注主体は送らない。 承認モードは画面で選ばない (issue #8620、 spec §5):
      // 確定時に行ごとのエリアの承認フロー設定から backend が解決する。
      const res = await checkBlock.mutateAsync({
        csvContent,
        csvFileName: file.name,
        // 列構成 (56 列 / 58 列) は CSV のヘッダーで決まる (AC-DSB-013) ので
        // 画面で選んだ種別は送らない。
        // 発注主体区分は CSV の行ごとの列 (spec spot-bid-block-csv-import.md AC-DSB-016) なので
        // 画面の発注主体は送らない。
        // #9007: 承認モードは確定時に backend が行のエリアから解決する。 旧 BFF が
        // 必須として検証するので、反映が揃うまで旧既定値を送る。
        approvalMode: BiddingApprovalMode.APPROVAL,
      })
      noticeBuildersRef.current.set(res.checkToken, (appliedCount) =>
        buildSpotBlockNotice(res.rows, appliedCount),
      )
      return {
        checkToken: res.checkToken,
        rows: toBlockPreviewRows(res.rows),
        importableCount: res.createdGroupCount,
        errorCount: res.errorCount,
        dateCounts: [],
        feeAutoConsiderUnavailable: res.feeAutoConsiderUnavailable,
        duplicateCount: res.duplicateGroupCount,
      }
    }
    // 通常入札は発注主体区分を CSV の行が持つので issuerType を送らない。
    // 承認モードも送らない (deprecated。 確定時に行ごとのエリアの承認フロー
    // 設定から backend が決める。 spot-bid-normal-csv-import.md §4 / §5)。
    const res = await checkSpot.mutateAsync({
      csvContent,
      csvFileName: file.name,
    })
    noticeBuildersRef.current.set(res.checkToken, (appliedCount) =>
      buildSpotNormalNotice(res.rows, appliedCount),
    )
    return {
      checkToken: res.checkToken,
      rows: toSpotPreviewRows(res.rows),
      importableCount: res.createdCount,
      errorCount: res.errorCount,
      // 日付の書式は、 行の受渡日 (toSpotPreviewRows) と同じ YYYY-MM-DD。
      dateCounts: toBidCsvDateCounts(res.dateCounts, civilDateToInputValue),
      feeAutoConsiderUnavailable: res.feeAutoConsiderUnavailable,
      duplicateCount: res.duplicateCount,
    }
  }

  const handleApply = async (checkToken: string): Promise<ApplyResult> => {
    // 確定する引換券の check 結果を持っていない時は、 取込結果を作れないので確定せずに止める
    // (引換券は消費しない)。
    const buildNotice = noticeBuildersRef.current.get(checkToken)
    if (buildNotice === undefined) {
      throw new ConnectError(
        t('common:upload.error.generic'),
        Code.FailedPrecondition,
      )
    }
    try {
      if (isBlock) {
        const res = await applyBlock.mutateAsync({ checkToken })
        return { notice: buildNotice(res.createdGroupCount) }
      }
      const res = await applySpot.mutateAsync({ checkToken })
      return { notice: buildNotice(res.createdCount) }
    } catch (err) {
      throw withBlockedReasonMessage(err)
    }
  }

  /**
   * 確定時に札の作成で拒否された (締切超過・入札制限違反など) とき、 backend が
   * 付けた理由 (ErrorInfo reason) を入札画面と同じ文言に差し替える (#6095)。
   * `UploadWizard` は ConnectError の rawMessage をそのまま出すため、 そのままだと
   * backend の英語の内部文字列が画面に出る。 code は保つので、 確定モーダルの
   * エラー表示 (FailedPrecondition) への切り替えは変わらない。 理由が付かない
   * 拒否 (行エラー・手数料単価の変更など CSV 固有の文言) はそのまま出す。
   */
  const withBlockedReasonMessage = (err: unknown): unknown => {
    if (!(err instanceof ConnectError)) return err
    const message = isBlock
      ? blockWriteBlockedReasonMessage(err, t)
      : spotWriteBlockedReasonMessage(err, t)
    if (message === undefined) return err
    return new ConnectError(message, err.code, err.metadata, undefined, err)
  }

  /**
   * Step1: 通常入札の雛形 (ヘッダー行 + spec §2「雛形の記入例データ」の 3 行)。
   * 表示言語が英語のときはヘッダー行・区分値・ファイル名も英語にする
   * (spec §2「英語表示対応」、 common.md §6)。
   * ブロック入札は下の種別ごとの雛形 (`blockTemplateDownloads`)。
   */
  const handleTemplateDownload = (): void => {
    downloadCsv(
      spotNormalCsvTemplateFileName(isEnglish),
      buildSpotNormalCsvTemplate(isEnglish),
    )
  }

  // ブロック入札の雛形は通常ブロック・リンクブロック・ループの 3 つ (spec §6、
  // #9332)。 ボタン名に列数は入れず、 ファイル名は表示言語で切り替える
  // (common.md §6)。 記入例は spec §2「雛形の記入例データ」の該当種別の行だけ。
  // 英語表示ではヘッダー行と記入例の区分値も英語にする (spec §2「英語表示対応」)。
  const blockTemplateDownloads = BLOCK_CSV_TYPES.map((type) => ({
    label: t(`page:spotCsvImport.template.${type}`),
    onClick: () => {
      downloadCsv(
        blockBidCsvTemplateFileName(type, isEnglish),
        buildBlockCsvTemplate(type, isEnglish),
      )
    },
  }))

  // 記入ガイドは通常ブロック・リンクブロック・ループの 3 表を並べる
  // (spec §3、 Des 回答 #8786)。 英語表示では英語の表にする (spec §3、 #9332)。
  const blockGuideSections = BLOCK_CSV_TYPES.map((type) => ({
    title: t(`page:spotCsvImport.guide.${type}`),
    rows: blockBidCsvColumnGuide(type, isEnglish),
  }))

  // 見出しと**揃え**を対で渡す。 8080 の列揃え規約 (`CLAUDE.md:332` /
  // `css/components.css:830`) は「固定値列 (enum / 時刻 / フラグ) は TH と揃えて
  // center、 数値は right、 可変テキストは left」。 受渡日・コマ・エリア・売買・
  // コマ範囲は固定値、 価格・数量は数値、 ペア (`P1 (STANDARD)`) だけが可変テキスト。
  const dataColumns: PreviewDataColumn[] = isBlock
    ? [
        { label: t('page:spotCsvImport.columns.targetDate'), align: 'center' },
        {
          label: t('page:spotCsvImport.columns.issuerType'),
          align: 'center',
        },
        { label: t('page:spotCsvImport.columns.area'), align: 'center' },
        { label: t('page:spotCsvImport.columns.side'), align: 'center' },
        { label: t('page:spotCsvImport.columns.price'), format: 'number' },
        { label: t('page:spotCsvImport.columns.komaRange'), align: 'center' },
        { label: t('page:spotCsvImport.columns.memoJepx') },
        { label: t('page:spotCsvImport.columns.memo') },
        { label: t('page:spotCsvImport.columns.pair') },
      ]
    : [
        { label: t('page:spotCsvImport.columns.targetDate'), align: 'center' },
        { label: t('page:spotCsvImport.columns.timeCode'), align: 'center' },
        { label: t('page:spotCsvImport.columns.issuerType'), align: 'center' },
        { label: t('page:spotCsvImport.columns.area'), align: 'center' },
        { label: t('page:spotCsvImport.columns.side'), align: 'center' },
        { label: t('page:spotCsvImport.columns.price'), format: 'number' },
        { label: t('page:spotCsvImport.columns.volume'), format: 'number' },
        // 可変テキスト (自由記述 / 組み立てた判別コメント) なので左寄せ。
        { label: t('page:spotCsvImport.columns.memoJepx') },
      ]

  return (
    <PageLayout
      breadcrumbItems={[
        { label: t('page:spotCsvImport.breadcrumb.bidding') },
        {
          label: t('page:spotCsvImport.breadcrumb.spot'),
          to: spotPagePath,
        },
        { label: t('page:spotCsvImport.breadcrumb.title'), bold: true },
      ]}
    >
      <PageBody>
        <UploadWizard<CheckResult, ApplyResult>
          acceptedHeaders={acceptedHeaders}
          // 入札 CSV の確定は結果を受け取るまで待つ (common.md AC-DUC-039〜041。
          // 共通仕様の非同期の確定の例外)。
          awaitsApplyResult
          canProceed={(result) =>
            result.errorCount === 0 && !result.feeAutoConsiderUnavailable
          }
          confirmDialog={{
            openLabel: t('page:spotCsvImport.confirmModal.open'),
            title: t('page:spotCsvImport.confirmModal.title'),
            confirmLabel: t('page:spotCsvImport.confirmModal.commit'),
            renderBody: (result) => (
              <VStack align="stretch" gap={2}>
                <Text>
                  {t(
                    isBlock
                      ? 'page:spotCsvImport.confirmModal.bodyBlock'
                      : 'page:spotCsvImport.confirmModal.bodyNormal',
                    {
                      target: t(
                        isBlock
                          ? 'page:spotCsvImport.confirmModal.target.block'
                          : 'page:spotCsvImport.confirmModal.target.normal',
                      ),
                    },
                  )}
                </Text>
                <Text textStyle="label">
                  {t(
                    isBlock
                      ? 'page:spotCsvImport.confirmModal.countBlock'
                      : 'page:spotCsvImport.confirmModal.countNormal',
                    { count: result.importableCount },
                  )}
                </Text>
              </VStack>
            ),
          }}
          // common.md §4: 業務バリデーションエラーがある間は確定へ進ませない
          // (確定側もエラー行 1 件で全体拒否するが、 進めてから弾くのは UX 上不正)。
          // ヘッダー行だけの CSV は Step1 で止まる (common.md AC-DUC-062)。
          //
          // 手数料自動考慮（スポット）が ON で手数料単価を取得できていない間も
          // 確定を無効化する (spot-bidding.md AC-BSP-004b「手数料自動考慮が ON で
          // 手数料単価を取得できない間は、 CSV 取込の確定操作を無効化し、 単価を
          // 取得できていない旨を表示する」)。
          displayHeader={displayHeader}
          // 確定モーダル: design `spot-csv-import.html` の文言 (通常入札は
          // 確定で承認申請・提出まで進む。 Des 回答 #8786)。
          // 件数は取込対象だけ — エラー件数は canProceed が 0 件を要求するので
          // モーダルでは常に 0 になり、 design の件数行にも無い。
          downloads={
            isBlock
              ? blockTemplateDownloads
              : [{ onClick: handleTemplateDownload }]
          }
          expectedHeader={expectedHeader}
          guideSections={isBlock ? blockGuideSections : undefined}
          maxFileSizeBytes={MAX_CSV_FILE_SIZE_BYTES}
          onApply={handleApply}
          onCheck={handleCheck}
          // 確定が成功したら、 すぐスポット入札画面へ移る。 移った先の成功のトーストが
          // 確認になる (AC-SPOT-024)。
          onComplete={(result) => {
            returnToBidPage(result.notice)
          }}
          // 確定時の再検証で取込全体を拒否されたら、 確定モーダルをエラー表示に
          // 切り替えて行番号つきのエラーを全件その場に出す (通常入札・ブロック入札
          // とも。 Des 回答 #8786)。
          renderApplyRejection={(message) => (
            <ConfirmRejection message={message} />
          )}
          renderSummary={(result) => (
            <VStack align="stretch" gap={3}>
              {/* AC-BSP-071: エラー行が存在する間は件数サマリを spec 規定文言に
                  差し替える (通常の「取込対象 N 件 / エラー N 件」表示はしない)。 */}
              {result.errorCount > 0 ? (
                <Alert
                  status="error"
                  title={t('page:spotCsvImport.summary.error', {
                    count: result.errorCount,
                  })}
                />
              ) : (
                <Text textStyle="label">
                  {t(
                    isBlock
                      ? 'page:spotCsvImport.summary.block'
                      : 'page:spotCsvImport.summary.normal',
                    {
                      importableCount: result.importableCount,
                      errorCount: result.errorCount,
                    },
                  )}
                </Text>
              )}
              {/* AC-SPOT-014: 通常入札は受渡日ごとの取込対象件数を出す (予約入札で
                  複数日を 1 ファイルに入れるため、 どの受渡日に何件入るかを確定前に
                  見せる)。 ブロック入札は空なので出ない。 */}
              <BidCsvDateCounts dateCounts={result.dateCounts} />
              {/* 手数料自動考慮 ON で手数料単価を取得できていない間、 確定操作
                  (canProceed) を無効化する理由を明示する (AC-BSP-004b)。 */}
              {result.feeAutoConsiderUnavailable && (
                <Alert
                  status="warning"
                  title={t('page:spotCsvImport.feeAutoConsiderUnavailable')}
                />
              )}
              {/* AC-SPOT-022 / AC-DSB-034: 既存の札と重複する行・グループの件数。 該当行には備考の列に
                  警告を添える。 警告があっても確定は止めない (canProceed に入れない)。 */}
              {result.duplicateCount > 0 && (
                <Alert
                  description={t(
                    isBlock
                      ? 'page:spotCsvImport.duplicateWarning.bodyBlock'
                      : 'page:spotCsvImport.duplicateWarning.bodyNormal',
                  )}
                  status="warning"
                  title={t('page:spotCsvImport.duplicateWarning.title', {
                    count: result.duplicateCount,
                  })}
                />
              )}
              <PreviewTable dataColumns={dataColumns} rows={result.rows} />
            </VStack>
          )}
          sampleRow={
            isBlock
              ? {
                  column: t('page:spotCsvImport.columns.targetDate'),
                  example: '2026/09/01',
                }
              : undefined
          }
          // AC-DUC-035 の {代表列} は表示用の列名 (英語表示では Delivery date。
          // 英語ヘッダー名の DeliveryDate ではない。 common.md 付録)。
        />
      </PageBody>
    </PageLayout>
  )
}
